import React from 'react';
import loadable from '@loadable/component';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

/**
 * Haul.Taxi's Terms of Service, owned in CODE rather than in a Console content asset — the same
 * code-ownership rationale already used for the landing page and the footer.
 *
 * Why: the hosted `content/pages/terms-of-service.json` asset shipped with Sharetribe's stock
 * placeholder ("Every marketplace business needs terms of service… use our template as a basis"),
 * and that placeholder was live in production. Keeping the real text here means it is versioned,
 * reviewable in a PR, and cannot silently revert to boilerplate.
 *
 * ⚠️ NOT LEGAL ADVICE, AND NOT COUNSEL-REVIEWED. This is an interim, plain-language description of
 * how the platform actually behaves, written to replace stock placeholder text. The long-form
 * drafts staged for an attorney live in `docs/legal/terms-of-service.md` alongside
 * `docs/legal/ATTORNEY-REVIEW-CHECKLIST.md`; those still need counsel to settle the legal entity,
 * governing law, arbitration/class-action terms, and the cancellation/refund policy. Replace this
 * text with the finalized version once that review lands.
 */

const LAST_UPDATED = 'August 13, 2026';

const termsMarkdown = `
_Last updated: ${LAST_UPDATED}_

These Terms govern your use of Haul.Taxi (the "Platform"). By creating an account, posting a job,
submitting a bid, or otherwise using the Platform, you agree to them. Please read them alongside our
[Privacy Policy](/privacy-policy).

We may update these Terms as the service changes. Continued use after an update means you accept the
revised Terms.

## 1. What Haul.Taxi is — and what it is not

Haul.Taxi is an online **marketplace**. It connects:

- **Customers** — people and businesses with junk, debris, or items that need hauling away; and
- **Haulers** — independent operators who bid to perform that work.

**Haul.Taxi is not a hauling or waste-removal company.** We do not own trucks, employ drivers, or
perform hauling. We do not take possession of your items. Haulers are **independent contractors**,
not our employees, agents, or partners. The agreement to perform a job is between the Customer and
the Hauler; we are not a party to it.

## 2. Who may use the Platform

You must be at least 18 and able to enter a binding contract. You are responsible for the accuracy of
the information you provide and for everything that happens under your account. Keep your password
confidential and tell us promptly if you suspect unauthorized use.

## 3. How the marketplace works

1. A Customer posts a job with photos of the items and a pickup address, optionally adding item
   categories, load size, access notes, and a budget.
2. Verified Haulers review the job and submit bids.
3. The Customer reviews bids and accepts the one they choose.
4. Payment is processed through our payments provider and the Hauler performs the job.

Posting a job is not an offer to any particular Hauler, and a bid is not binding until the Customer
accepts it. A standing bid expires automatically if it goes 24 hours without activity.

## 4. Hauler verification — what it does and does not mean

Before a Hauler can bid, they submit vehicle and insurance documentation, which we review and either
approve or reject. A "verified" badge means **only** that we reviewed those submitted documents and
approved that account to bid.

It is **not** a guarantee of a Hauler's licensing, insurance coverage at any moment in time, work
quality, safety, honesty, or suitability for your job. We do not perform criminal background checks.
Some Haulers may additionally complete identity verification through our payments provider, but this
is not required of every Hauler. **Customers should exercise their own judgement**, including
confirming insurance directly for high-value or high-risk work.

## 5. Pricing, fees, and payment

- Haulers set their own bid prices. Customers pay the accepted bid.
- A minimum bid amount of **$25** applies.
- Haul.Taxi retains a **20% commission**, deducted from the Hauler's payout. Customers pay no
  separate platform fee.
- Payments are processed by **Stripe**. We do not store your full card details. Haulers receive
  payouts through Stripe Connect and must complete Stripe's onboarding, including any identity and
  bank verification Stripe requires, before they can be paid.
- Haulers are solely responsible for their own taxes. We do not withhold taxes on their behalf.

## 6. AI job scoping is a suggestion only

The Platform may use AI to read the photos and job details you provide in order to describe the job —
what kind of work it is, what items are involved, and what equipment a Hauler is likely to need — so
we can route it to suitable Haulers. **This is an automated guess, not a quote, an offer, a
guarantee, or a safety assessment**, and it does not bind any Hauler or us. It does not set or
suggest a price; prices come only from Hauler bids. A Hauler confirms the actual scope of work. Where
the AI flags materials that may need special handling, that flag is generated from photos and is
**not** a substitute for telling your Hauler about hazardous or regulated material (see section 8).
To do this we send the photos and job details you submit to a third-party AI provider — see the
[Privacy Policy](/privacy-policy).

## 7. Cancellations, changes, and disputes

Plans change. If you need to cancel or reschedule, do so as early as possible and communicate through
the Platform so there is a record. If a job goes wrong — work not performed, work not as described, a
disagreement over price or damage — contact us and we will try to help the parties resolve it.

**We are not the insurer of any job and we are not obliged to compensate either party.** Disputes are
ultimately between the Customer and the Hauler. Payment disputes may also be subject to the rules of
your card issuer and our payments provider.

## 8. Prohibited and restricted items

Customers must **not** ask a Hauler to remove, and Haulers must not accept, anything the Hauler is not
legally permitted and equipped to handle. This includes hazardous or regulated materials such as
asbestos, chemicals, solvents, paint, fuel, oil, batteries, medical or biohazardous waste, ammunition
or explosives, and refrigerants requiring certified recovery.

**If your job involves hazardous or regulated material, you must disclose it up front.** You are
responsible for the accuracy of that disclosure and for any loss, penalty, or clean-up cost arising
from an undisclosed or misdescribed item. Haulers must dispose of everything lawfully and at properly
licensed facilities.

## 9. Your content

You keep ownership of the photos, messages, reviews, and other content you post. You grant us a
non-exclusive licence to host, display, and use it to operate and promote the Platform. Do not post
content you lack the rights to, and do not include other people's personal information. Remember that
photos of a pickup location may reveal information about your home — consider what is in frame.

## 10. Acceptable use

Do not use the Platform to break the law, harass anyone, discriminate, misrepresent who you are,
circumvent the Platform's fees by arranging payment off-platform, scrape or overload the service, or
interfere with its security. We may suspend or terminate accounts that do — including for conduct
that puts other users at risk.

## 11. Reviews

Reviews must reflect a genuine experience. Do not post false, retaliatory, or incentivised reviews. We
may remove reviews that violate these Terms.

## 12. Availability and changes to the service

We aim to keep the Platform available but do not guarantee uninterrupted or error-free service. We may
change, suspend, or discontinue features at any time.

## 13. Disclaimers

The Platform is provided **"as is" and "as available."** To the fullest extent permitted by law, we
disclaim all warranties, express or implied, including merchantability, fitness for a particular
purpose, and non-infringement.

**We do not warrant the conduct, reliability, qualifications, insurance, or work of any Hauler, or the
accuracy of any job description posted by a Customer.**

## 14. Limitation of liability

To the fullest extent permitted by law, Haul.Taxi is not liable for indirect, incidental,
consequential, special, exemplary, or punitive damages, or for lost profits, lost data, personal
injury, or property damage arising from a job arranged through the Platform.

Where liability cannot lawfully be excluded, our total aggregate liability is limited to the greater
of the commission we actually earned on the transaction giving rise to the claim, or **$100**.

## 15. Indemnity

You agree to indemnify and hold harmless Haul.Taxi and its operators from claims, damages, and costs
(including reasonable legal fees) arising from your use of the Platform, your breach of these Terms,
your violation of law, or — for Customers — undisclosed hazardous or prohibited items.

## 16. Termination

You may stop using the Platform and delete your account at any time from your account settings. We may
suspend or terminate access if you breach these Terms or if we reasonably believe it is necessary to
protect users or the Platform. Obligations that by their nature should survive termination will
survive.

## 17. Governing law and disputes

These Terms are governed by the laws of the United States and of the state in which Haul.Taxi's
operator is established, without regard to conflict-of-laws rules. **A specific governing-law
jurisdiction and any binding arbitration or class-action-waiver terms are still being finalised and
will be stated here when they are.** Until then, nothing in these Terms limits any right you have that
cannot lawfully be waived.

## 18. Contact

Questions about these Terms: **haultaxi@gmail.com**
`;

// Create the page content (array of sections) in page-asset format.
export const fallbackSections = {
  sections: [
    {
      sectionType: 'article',
      sectionId: 'terms',
      appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
      title: { fieldType: 'heading1', content: 'Terms of Service' },
      blocks: [
        {
          blockType: 'defaultBlock',
          blockId: 'terms-content',
          text: {
            fieldType: 'markdown',
            content: termsMarkdown,
          },
        },
      ],
    },
  ],
  meta: {
    pageTitle: {
      fieldType: 'metaTitle',
      content: 'Terms of Service | Haul.Taxi',
    },
    pageDescription: {
      fieldType: 'metaDescription',
      content:
        'The terms that govern use of the Haul.Taxi marketplace for customers and independent haulers.',
    },
  },
};

const FallbackPage = props => {
  return <PageBuilder pageAssetsData={fallbackSections} {...props} />;
};

export default FallbackPage;
